/**
 * Position Manager Utility
 *
 * Provides position calculation functions for the chatbot popover.
 *
 * Usage:
 *   import { getResponsiveSize, calculatePopoverPosition } from '@/components/OliverBotV2/utils/positionManager.js'
 *
 *   const size = getResponsiveSize(viewport, deviceType, defaultSize)
 *   const position = calculatePopoverPosition(buttonPos, viewport, size, margin)
 */

/**
 * Get responsive popover size based on device type and viewport
 * @param {Object} viewport - {width, height}
 * @param {string} deviceType - 'mobile' | 'tablet' | 'desktop'
 * @param {Object} defaultSize - {width, height} default sizes
 * @returns {Object} - {width, height}
 */
export function getResponsiveSize(viewport, deviceType, defaultSize) {
  let maxWidth = defaultSize.width;
  let maxHeight = defaultSize.height;

  if (deviceType === "mobile") {
    // Mobile: nearly full screen
    maxWidth = viewport.width - 20;
    maxHeight = viewport.height - 80;
  } else if (viewport.width >= 769 && viewport.width <= 1024) {
    // Tablet
    maxWidth = Math.min(maxWidth, 420);
    maxHeight = Math.min(viewport.height - 120, maxHeight);
  } else if (viewport.width >= 1025) {
    // Desktop
    maxWidth = Math.min(maxWidth, 500);
    maxHeight = Math.min(maxHeight, 700);
  }

  return { width: maxWidth, height: maxHeight };
}

/**
 * Calculate popover position based on button position (desktop)
 * @param {Object} buttonPosition - {x, y} button position (right/bottom)
 * @param {Object} viewport - {width, height}
 * @param {Object} size - {width, height} popover size
 * @param {number} minMargin - Minimum margin from viewport edges
 * @param {number} buttonOffset - Offset from button
 * @returns {Object} - {left, top}
 */
export function calculatePopoverPosition(
  buttonPosition,
  viewport,
  size,
  minMargin,
  buttonOffset
) {
  // Calculate initial left position
  let leftPosition = viewport.width - buttonPosition.x - size.width;
  if (leftPosition < minMargin) {
    leftPosition = minMargin;
  }

  // Calculate initial top position based on button position
  let topPosition =
    viewport.height - buttonPosition.y - buttonOffset - size.height;

  // Ensure popover stays within viewport bounds
  if (topPosition < minMargin) {
    topPosition = minMargin;
  }
  if (topPosition + size.height > viewport.height - minMargin) {
    topPosition = viewport.height - size.height - minMargin;
  }

  return { left: leftPosition, top: topPosition };
}

/**
 * Calculate centered popover position (mobile)
 * @param {Object} viewport - {width, height}
 * @param {Object} size - {width, height} popover size
 * @returns {Object} - {left, top}
 */
export function calculateCenteredPosition(viewport, size) {
  const leftPosition = (viewport.width - size.width) / 2;
  const topPosition = (viewport.height - size.height) / 2;

  return { left: leftPosition, top: topPosition };
}

/**
 * Create position styles object
 * @param {Object} position - {left, top}
 * @param {Object} size - {width, height}
 * @param {string} transformOrigin - CSS transform-origin value
 * @param {number} zIndex - z-index value
 * @returns {Object} - Vue style object
 */
export function createPositionStyles(
  position,
  size,
  transformOrigin = "bottom center",
  zIndex = 1001
) {
  return {
    position: "fixed",
    left: `${position.left}px`,
    top: `${position.top}px`,
    width: `${size.width}px`,
    height: `${size.height}px`,
    zIndex,
    transformOrigin,
  };
}

/**
 * Create fullscreen styles
 * @param {Object} viewport - {width, height}
 * @param {number} margin - Margin from viewport edges
 * @param {number} zIndex - z-index value
 * @returns {Object} - Vue style object
 */
export function createFullscreenStyles(viewport, margin, zIndex = 1001) {
  return {
    position: "fixed",
    left: `${margin}px`,
    top: `${margin}px`,
    width: `${viewport.width - margin * 2}px`,
    height: `${viewport.height - margin * 2}px`,
    zIndex,
    maxWidth: "none",
    maxHeight: "none",
    transformOrigin: "bottom center",
  };
}

/**
 * Compute the effective minimum popover size for the current viewport.
 * Floors are relaxed to viewport when the viewport is smaller than the preferred minimum
 * (so the popover never refuses to render on small screens).
 * @param {Object} viewport - {width, height}
 * @param {number} minMargin - Margin from viewport edges
 * @param {number} preferredMinWidth - Desired hard floor width
 * @param {number} preferredMinHeight - Desired hard floor height
 * @returns {Object} - {width, height}
 */
export function getEffectiveMinSize(
  viewport,
  minMargin,
  preferredMinWidth,
  preferredMinHeight
) {
  return {
    width: Math.min(
      preferredMinWidth,
      Math.max(0, viewport.width - minMargin * 2)
    ),
    height: Math.min(
      preferredMinHeight,
      Math.max(0, viewport.height - minMargin * 2)
    ),
  };
}

/**
 * Clamp a size into [effectiveMin, viewport - 2*margin] on each axis.
 * @param {Object} size - {width, height}
 * @param {Object} viewport - {width, height}
 * @param {number} minMargin - Margin from viewport edges
 * @param {number} preferredMinWidth - Desired hard floor width
 * @param {number} preferredMinHeight - Desired hard floor height
 * @returns {Object} - {width, height} with both values rounded to ints
 */
export function clampSize(
  size,
  viewport,
  minMargin,
  preferredMinWidth,
  preferredMinHeight
) {
  const min = getEffectiveMinSize(
    viewport,
    minMargin,
    preferredMinWidth,
    preferredMinHeight
  );
  const maxW = Math.max(min.width, viewport.width - minMargin * 2);
  const maxH = Math.max(min.height, viewport.height - minMargin * 2);
  return {
    width: Math.round(Math.min(Math.max(size.width, min.width), maxW)),
    height: Math.round(Math.min(Math.max(size.height, min.height), maxH)),
  };
}
